import React from 'react';
import PropTypes from 'prop-types';
import { RadiosButton, TextField } from '../../../../../01_atoms/FormElements';
import { parseOnlyDigits } from '../../../../../../utils/formFieldParsers';
import { composeValidators, minValue, required } from '../../../../../../utils/formFieldValidators';
import { currencySymbolPath, DEFAULT_CURRENCY, getSymbol } from '../../../../../../utils/currency';
import { useForm, useField, useFormState } from 'react-final-form';
import ImpactText from '../../../../../01_atoms/ImpactText';
import { getImpact } from '../../../../../../utils/donationForm';
import { composeParsers, parseLengthLimit } from '../../../../../../utils/formFieldParsers';
import ValidationErrors from '../../../../../01_atoms/DonationFormAlert/ValidationErrors';

import styles from './index.module.scss';
import FormattedText from '../../../../../01_atoms/FormattedText';
import RadioButtonGroup from '../../../../../01_atoms/FormElements/RadioButtonGroup';

const MoneyHandles = ({
  optionsSingle,
  optionsMonthly,
  impactsSingleDonation,
  impactsMonthlyDonation,
  minAmount = 1,
  style,
  monthlyInfoText = '',
}) => {
  const form = useForm();
  const formState = useFormState();
  const currentDonationType = formState.values['donation_type'];
  const currentAmount = formState.values['amount'][currentDonationType];
  const moneyHandleOptions = currentDonationType === 'monthly' ? optionsMonthly : optionsSingle;
  const impacts =
    currentDonationType === 'monthly' ? impactsMonthlyDonation : impactsSingleDonation;
  const amountField = useField(`amount.${currentDonationType}`, {
    type: 'text',
    parse: composeParsers(parseOnlyDigits, parseLengthLimit(8)),
  });
  const moneyHandleField = useField(`money_handle.${currentDonationType}`, { type: 'radio' });
  const showTabs = !!optionsSingle.length && !!optionsMonthly.length;

  let currentImpact;
  if (currentAmount) {
    currentImpact = getImpact(+currentAmount, impacts);
  }

  if (!currentImpact) {
    // Keep the impact with the last know valid amount.
    if (
      formState.values.money_handle?.last_valid &&
      formState.values.money_handle?.last_valid[currentDonationType]
    ) {
      currentImpact = getImpact(
        +formState.values.money_handle.last_valid[currentDonationType],
        impacts,
      );
    } else {
      // When amount passed through query, display default impact message.
      currentImpact = getImpact(
        +formState.initialValues.money_handle[formState.initialValues.donation_type],
        impacts,
      );
    }
  }

  const onKeyDownPress = (e, item, mutateFormValue) => {
    const enter = 13;
    if (e.keyCode === enter && mutateFormValue) {
      // Updates checked state
      mutateFormValue(`money_handle.${currentDonationType}`, item.value);
      // Updates custom amount field on money handle change.
      mutateFormValue(`amount.${currentDonationType}`, item.value);
    }
  };

  const onMoneyHandleChange = (event, input) => {
    // Call default onChange handler.
    input.onChange(event);
    // Updates custom amount field on money handle change.
    form.mutators.setValue(`amount.${currentDonationType}`, event.target.value);
  };

  // Update selected money handle on custom amount change.
  const onAmountChange = (event) => {
    // Call default onChange handler.
    amountField.input.onChange(event);

    const moneyHandleIndex = moneyHandleOptions.findIndex((i) => i.value === event.target.value);

    if (moneyHandleIndex === -1) {
      // Store the last valid amount from the money handles to keep impact message for it.
      const impactIndex = impacts.findIndex(
        (i) => i.amount === +formState.values.amount[currentDonationType],
      );
      if (impactIndex >= 0) {
        form.mutators.setValue(
          `money_handle.last_valid.${currentDonationType}`,
          formState.values.amount[currentDonationType],
        );
      }
      // Reset radio button.
      form.mutators.setValue(`money_handle.${currentDonationType}`, undefined);
    } else {
      form.mutators.setValue(
        `money_handle.${currentDonationType}`,
        moneyHandleOptions[moneyHandleIndex].value,
      );
    }
  };

  const showMontlyInfoText = showTabs && !!monthlyInfoText && currentDonationType === 'monthly';

  return (
    <div className={`${styles['money-handles']} ${styles[`money-handles--${style}`]}`}>
      {showTabs && (
        <div className={styles['money-handles__donation-type']}>
          <RadioButtonGroup
            name="donation_type"
            mutateFormValue={form.mutators.setValue}
            options={[
              { value: 'single', label: 'One-off' },
              { value: 'monthly', label: 'Give monthly' },
            ]}
            size="extra"
            styling={style}
          />
          {showMontlyInfoText && (
            <div className={styles['money-handles__monthly-info']}>
              <FormattedText text={monthlyInfoText} />
            </div>
          )}
        </div>
      )}

      <div
        className={`${styles['money-handles__container']} ${
          showTabs ? styles['money-handles__container--with-tabs'] : ''
        }`}
      >
        {formState.submitFailed && formState.hasValidationErrors && (
          <ValidationErrors errors={formState.errors} />
        )}
        {!showMontlyInfoText && (
          <div className={styles['money-handles__heading']}>
            Make a <strong>{currentDonationType === 'monthly' ? 'monthly' : 'one-off'}</strong>{' '}
            donation of
          </div>
        )}

        <div className={styles['money-handles__wrapper']}>
          <div className={styles['money-handles__inner']}>
            <RadiosButton
              name={`money_handle.${currentDonationType}`}
              size="large"
              onKeyDown={onKeyDownPress}
              mutateFormValue={form.mutators.setValue}
              options={moneyHandleOptions}
              onChange={(ev) => onMoneyHandleChange(ev, moneyHandleField.input)}
              styling={style === 'default' ? 'default' : 'emergency-black'}
            />

            {currentImpact && (
              <ImpactText
                {...currentImpact}
                className={`${styles['money-handles__impact']} ${styles['money-handles__impact--mobile']}`}
                styling={style}
              />
            )}

            <div>
              <div className={styles['money-handles__custom-amount-label']}>
                Or enter your own amount
              </div>
              <TextField
                name={`amount.${currentDonationType}`}
                validate={composeValidators(
                  required('Please add a donation amount'),
                  minValue(minAmount, `Please enter an amount greater than ${minAmount - 1}`),
                )}
                onChange={(ev) => onAmountChange(ev)}
                icon={currencySymbolPath[DEFAULT_CURRENCY]}
                autoComplete="off"
                isRequired
                label=""
                placeholder=""
                // Enable numeric keyboard for mobile devices.
                inputMode="numeric"
                pattern="[0-9]+"
                infoOnPatternNotMatched="Please use numbers only"
                aria-label={getSymbol(DEFAULT_CURRENCY)}
              />
            </div>
          </div>
          {currentImpact && (
            <ImpactText
              {...currentImpact}
              className={`${styles['money-handles__impact']}  ${styles['money-handles__impact--desktop']}`}
              styling={style}
            />
          )}
        </div>
      </div>
    </div>
  );
};

MoneyHandles.propTypes = {
  optionsSingle: PropTypes.array.isRequired,
  optionsMonthly: PropTypes.array.isRequired,
  impactsSingleDonation: PropTypes.array.isRequired,
  impactsMonthlyDonation: PropTypes.array.isRequired,
  minAmount: PropTypes.number,
  style: PropTypes.oneOf(['default', 'emergency']).isRequired,
  monthlyInfoText: PropTypes.string,
};

export default MoneyHandles;
